import React from 'react';
import './App.css';
import Man from "./Pages/Man";
import Woman from "./Pages/Woman";

type Side = 'lui' | 'lei';

const SIDES = Object.freeze<Record<Side, {
  page: 'Man' | 'Woman',
  label: string,
  text: string,
  background: string,
  position: string,
  border: string,
  button: string
}>>({
  lui: {
    page: 'Man',
    label: 'Lui',
    text: 'Trattamenti su misura per lui.',
    background: 'bg-header-background',
    position: 'bg-[position:49%_50%]', // keeps the face centred in the open panel
    border: 'hover:border-quaternary',
    button: 'bg-primary hover:bg-quaternary'
  },
  lei: {
    page: 'Woman',
    label: 'Lei',
    text: 'Trattamenti su misura per lei.',
    background: 'bg-header-background-woman',
    position: 'bg-[position:56%_50%]',
    border: 'hover:border-accentWoman',
    button: 'bg-primaryWoman hover:bg-accentWoman'
  }
});

// Booking platform, also used for login (same link as "Clicca e Prenota Ora" in the pages)
const BOOKING_LINK = 'https://tribeauty.it/MR.TREWAN';

// Same as Tailwind's 2xl breakpoint: below it the hero is the mobile split
const isDesktop = () => window.matchMedia('(min-width: 1536px)').matches;

// ?per=lui / ?per=lei picks the side that opens first (ad links, QR codes); Lei by default
const initialSide = (): Side => new URLSearchParams(window.location.search).get('per') === 'lui' ? 'lui' : 'lei';

function App() {


  const [page, setPage] = React.useState('None');
  const [open, setOpen] = React.useState<Side>(initialSide);

  if(page === "Man") return <Man onSwitch={() => setPage("Woman")}/>
  if(page === "Woman") return <Woman onSwitch={() => setPage("Man")}/>

  const panel = (side: Side) => {
    const s = SIDES[side];
    const isOpen = open === side;
    return (
      <section
        className={`relative overflow-hidden transition-[flex-grow,flex-basis] duration-[650ms] ease-[cubic-bezier(.65,0,.25,1)]
          ${isOpen ? 'flex-[1_1_0%] cursor-default' : 'flex-[0_0_64px] cursor-pointer'}
          2xl:flex-[1_1_0%] 2xl:h-[100vh] 2xl:cursor-pointer 2xl:border-4 border-black ${s.border}`}
        onClick={() => {
          // Desktop: straight to the page. Mobile: tapping the strip swaps, tapping the open panel does nothing
          if (isDesktop()) setPage(s.page);
          else if (!isOpen) setOpen(side);
        }}
      >
        {/* Image is always viewport-wide and centred, so resizing the panel just reveals/hides the sides */}
        <div className={`absolute inset-y-0 left-1/2 w-[100vw] -translate-x-1/2 ${s.background} bg-cover bg-no-repeat ${s.position} pointer-events-none
          2xl:inset-0 2xl:w-full 2xl:translate-x-0 2xl:bg-center`}/>

        {/* Mobile: bottom fade for legibility */}
        <div className="absolute inset-x-0 top-[55%] bottom-0 bg-gradient-to-b from-transparent to-black/[.55] pointer-events-none 2xl:hidden"/>

        {/* Mobile: vertical label shown on the collapsed strip */}
        <span
          className={`absolute inset-0 z-[3] flex items-center justify-center [writing-mode:vertical-rl] bg-black/[.45] text-white text-xs font-medium tracking-[.4em] uppercase pointer-events-none transition-opacity duration-300 2xl:hidden
            ${side === 'lui' ? 'rotate-180' : ''}
            ${isOpen ? 'opacity-0' : 'opacity-100 delay-[250ms]'}`}
        >
          {s.label}
        </span>

        {/* Mobile: content revealed in the open panel */}
        <div
          className={`absolute inset-x-0 bottom-0 z-[2] px-6 pb-[calc(30px+env(safe-area-inset-bottom))] text-left text-white transition-[opacity,transform] duration-[350ms] 2xl:hidden
            ${isOpen ? 'opacity-100 translate-y-0 delay-[350ms]' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        >
          <h2 className="font-serif font-medium text-[44px] leading-none mb-2.5">{s.label}</h2>
          <p className="text-[15px] opacity-90 mb-[22px]">{s.text}</p>
          <button
            className="inline-block border border-white/90 rounded-sm py-3.5 pl-[26px] pr-6 text-2xs font-medium tracking-[.16em] uppercase active:opacity-60"
            onClick={e => { e.stopPropagation(); setPage(s.page); }}
          >
            Scopri
          </button>
        </div>

        {/* Desktop: centred button */}
        <div className="relative hidden 2xl:flex h-full items-center justify-center">
          <div className="h-1/2 flex justify-end items-end">
            <button className={`${s.button} rounded-lg border-2 py-2 px-4 w-[200px] text-white`}>{s.label}</button>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="App flex flex-col h-[100vh] supports-[height:100dvh]:h-[100dvh] 2xl:block 2xl:!h-auto">
      {/* Mobile: logo left, Accedi / Prenota ora right. Desktop: logo only, centred */}
      <nav
        className={`bg-black w-full flex justify-between 2xl:justify-center items-center shrink-0 relative z-[5]
          h-[calc(56px+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] px-4 2xl:h-auto 2xl:p-3`}
      >
        {/* Logo works as "home": back to the default side without reloading */}
        <a href="./" onClick={e => { if (!isDesktop()) { e.preventDefault(); setOpen('lei'); } }}>
          <div className="h-[37.5px] w-[75px] bg-logo-white bg-image"/>
        </a>
        <div className="flex items-center gap-2 2xl:hidden">
          <a
            href={BOOKING_LINK} target="_blank" rel="noreferrer"
            className="min-h-[44px] inline-flex items-center px-1.5 text-white/[.85] text-2xs font-medium tracking-[.14em] uppercase whitespace-nowrap active:opacity-60"
          >
            Accedi
          </a>
          {/* Full-size tap target around a smaller outlined box; less right padding offsets the trailing letter-spacing */}
          <a
            href={BOOKING_LINK} target="_blank" rel="noreferrer"
            className="min-h-[44px] inline-flex items-center text-white text-2xs font-medium tracking-[.14em] uppercase whitespace-nowrap active:opacity-60"
          >
            <span className="py-[9px] pl-3.5 pr-3 border border-white/[.85] rounded-sm">Prenota ora</span>
          </a>
        </div>
      </nav>
      <main className="flex-1 min-h-0 w-full flex flex-row gap-1 bg-black 2xl:gap-0">
        {panel('lui')}
        {panel('lei')}
      </main>
    </div>
  );
}

export default App;
